import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { LicenseStatusCard } from "@/components/LicenseManager";
import { 
  Settings, 
  Palette, 
  Image, 
  Save, 
  RotateCcw, 
  ShieldAlert, 
  Mail, 
  Send, 
  Eye, 
  EyeOff,
  CheckCircle,
  XCircle,
  Loader2,
  Key
} from "lucide-react";

const API = '/api';

export default function SettingsPage() {
  const { isAdmin, getAuthHeader } = useAuth();
  const { refreshSettings, applyTheme } = useSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [settings, setSettings] = useState({
    primary_color: "#1B3B6F",
    secondary_color: "#C9A227",
    sidebar_color: "#1B3B6F",
    logo_url: "",
    organization_name: "NMLRA",
    // SMTP Settings
    smtp_enabled: false,
    smtp_host: "",
    smtp_port: "587",
    smtp_username: "",
    smtp_password: "",
    smtp_encryption: "tls",
    smtp_from_email: "",
    smtp_from_name: "",
  });
  const [originalSettings, setOriginalSettings] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      const data = res.data;
      const newSettings = {
        primary_color: data.primary_color || "#1B3B6F",
        secondary_color: data.secondary_color || "#C9A227",
        sidebar_color: data.sidebar_color || "#1B3B6F",
        logo_url: data.logo_url || "",
        organization_name: data.organization_name || "NMLRA",
        smtp_enabled: data.smtp_enabled || false,
        smtp_host: data.smtp_host || "",
        smtp_port: data.smtp_port || "587",
        smtp_username: data.smtp_username || "",
        smtp_password: data.smtp_password || "",
        smtp_encryption: data.smtp_encryption || "tls",
        smtp_from_email: data.smtp_from_email || "",
        smtp_from_name: data.smtp_from_name || "",
        smtp_password_set: data.smtp_password_set || false,
      };
      setSettings(newSettings);
      setOriginalSettings(newSettings);
    } catch (error) {
      console.error("Failed to fetch settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: getAuthHeader(),
      });
      setOriginalSettings(settings);
      // Apply theme changes immediately
      applyTheme(settings);
      // Refresh global settings context
      refreshSettings();
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm("Reset all settings to default values?")) return;
    
    const defaults = {
      primary_color: "#1B3B6F",
      secondary_color: "#C9A227",
      sidebar_color: "#1B3B6F",
      logo_url: "",
      organization_name: "NMLRA",
      smtp_enabled: false,
      smtp_host: "",
      smtp_port: "587",
      smtp_username: "",
      smtp_password: "",
      smtp_encryption: "tls",
      smtp_from_email: "",
      smtp_from_name: "",
    };
    
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, defaults, {
        headers: getAuthHeader(),
      });
      setSettings(defaults);
      setOriginalSettings(defaults);
      toast.success("Settings reset to defaults!");
    } catch (error) {
      toast.error("Failed to reset settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error("Please enter a test email address");
      return;
    }
    
    // Save settings first if there are changes
    if (hasChanges) {
      toast.error("Please save your settings before testing");
      return;
    }
    
    setTestingEmail(true);
    try {
      await axios.post(`${API}/settings/test-email`, 
        { test_email: testEmail },
        { headers: getAuthHeader() }
      );
      toast.success(`Test email sent to ${testEmail}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  if (!isAdmin()) {
    return (
      <div data-testid="settings-page" className="space-y-6">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Settings
          </h1>
          <p className="text-slate-500 mt-1">Customize app appearance</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <ShieldAlert className="mx-auto text-amber-500 mb-4" size={48} />
            <h3 className="font-semibold text-lg mb-2">Admin Access Required</h3>
            <p className="text-slate-500">
              Only administrators can modify settings.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Settings
          </h1>
          <p className="text-slate-500 mt-1">Customize app colors, branding, and email settings</p>
        </div>

        <div className="flex gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw size={18} className="mr-2" />
              Undo Changes
            </Button>
          )}
          <Button 
            onClick={handleSave} 
            disabled={!hasChanges || saving}
            className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
          >
            <Save size={18} className="mr-2" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Colors */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Palette className="text-[#1B3B6F]" />
              Colors
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="primary_color">Primary Color</Label>
              <p className="text-xs text-slate-500 mb-2">Used for buttons, headers, and accents</p>
              <div className="flex gap-3">
                <Input
                  id="primary_color"
                  type="color"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  className="w-16 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={settings.primary_color}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  placeholder="#1B3B6F"
                  className="flex-1 font-mono"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="secondary_color">Secondary Color</Label>
              <p className="text-xs text-slate-500 mb-2">Used for highlights and gold accents</p>
              <div className="flex gap-3">
                <Input
                  id="secondary_color"
                  type="color"
                  value={settings.secondary_color}
                  onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                  className="w-16 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={settings.secondary_color}
                  onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                  placeholder="#C9A227"
                  className="flex-1 font-mono"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="sidebar_color">Sidebar Color</Label>
              <p className="text-xs text-slate-500 mb-2">Background color for the navigation sidebar</p>
              <div className="flex gap-3">
                <Input
                  id="sidebar_color"
                  type="color"
                  value={settings.sidebar_color}
                  onChange={(e) => setSettings({ ...settings, sidebar_color: e.target.value })}
                  className="w-16 h-10 p-1 cursor-pointer"
                />
                <Input
                  type="text"
                  value={settings.sidebar_color}
                  onChange={(e) => setSettings({ ...settings, sidebar_color: e.target.value })}
                  placeholder="#1B3B6F"
                  className="flex-1 font-mono"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Branding */}
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Image className="text-[#1B3B6F]" />
              Branding
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="organization_name">Organization Name</Label>
              <p className="text-xs text-slate-500 mb-2">Displayed in headers and reports</p>
              <Input
                id="organization_name"
                value={settings.organization_name}
                onChange={(e) => setSettings({ ...settings, organization_name: e.target.value })}
                placeholder="NMLRA"
              />
            </div>

            <div>
              <Label htmlFor="logo_url">Logo URL</Label>
              <p className="text-xs text-slate-500 mb-2">Enter URL to your logo image (PNG, JPG recommended)</p>
              <Input
                id="logo_url"
                value={settings.logo_url}
                onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>

            {/* Logo Preview */}
            {settings.logo_url && (
              <div className="p-4 bg-slate-100 rounded-lg">
                <p className="text-sm text-slate-600 mb-2">Logo Preview:</p>
                <div className="bg-white p-4 rounded border flex items-center justify-center">
                  <img 
                    src={settings.logo_url} 
                    alt="Logo preview" 
                    className="max-h-20 max-w-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      toast.error("Failed to load logo image");
                    }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Color Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl">Color Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            <div 
              className="p-6 rounded-lg text-white text-center"
              style={{ backgroundColor: settings.primary_color }}
            >
              <p className="font-semibold">Primary</p>
              <p className="text-sm opacity-80">{settings.primary_color}</p>
            </div>
            <div 
              className="p-6 rounded-lg text-white text-center"
              style={{ backgroundColor: settings.secondary_color }}
            >
              <p className="font-semibold">Secondary</p>
              <p className="text-sm opacity-80">{settings.secondary_color}</p>
            </div>
            <div 
              className="p-6 rounded-lg text-white text-center"
              style={{ backgroundColor: settings.sidebar_color }}
            >
              <p className="font-semibold">Sidebar</p>
              <p className="text-sm opacity-80">{settings.sidebar_color}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings (Post SMTP) */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Mail className="text-[#1B3B6F]" />
            Email Settings (SMTP)
            {settings.smtp_enabled ? (
              <span className="ml-2 flex items-center text-sm font-normal text-green-600">
                <CheckCircle size={16} className="mr-1" /> Enabled
              </span>
            ) : (
              <span className="ml-2 flex items-center text-sm font-normal text-slate-400">
                <XCircle size={16} className="mr-1" /> Disabled
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label htmlFor="smtp_enabled" className="text-base font-medium">Enable SMTP Email</Label>
              <p className="text-sm text-slate-500">Send password reset emails via your SMTP server</p>
            </div>
            <Switch
              id="smtp_enabled"
              checked={settings.smtp_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, smtp_enabled: checked })}
            />
          </div>

          <div className={`grid md:grid-cols-2 gap-6 ${!settings.smtp_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <Label htmlFor="smtp_host">SMTP Host</Label>
              <p className="text-xs text-slate-500 mb-2">e.g., smtp.gmail.com, smtp.office365.com</p>
              <Input
                id="smtp_host"
                value={settings.smtp_host}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                placeholder="smtp.example.com"
              />
            </div>

            <div>
              <Label htmlFor="smtp_port">SMTP Port</Label>
              <p className="text-xs text-slate-500 mb-2">Common ports: 587 (TLS), 465 (SSL), 25</p>
              <Input
                id="smtp_port"
                value={settings.smtp_port}
                onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                placeholder="587"
              />
            </div>

            <div>
              <Label htmlFor="smtp_username">SMTP Username</Label>
              <p className="text-xs text-slate-500 mb-2">Usually your email address</p>
              <Input
                id="smtp_username"
                value={settings.smtp_username}
                onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                placeholder="user@example.com"
              />
            </div>

            <div>
              <Label htmlFor="smtp_password">SMTP Password</Label>
              <p className="text-xs text-slate-500 mb-2">Your email password or app-specific password</p>
              <div className="relative">
                <Input
                  id="smtp_password"
                  type={showPassword ? "text" : "password"}
                  value={settings.smtp_password}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  placeholder={settings.smtp_password_set ? "••••••••" : "Enter password"}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="smtp_encryption">Encryption</Label>
              <p className="text-xs text-slate-500 mb-2">Security protocol for connection</p>
              <select
                id="smtp_encryption"
                value={settings.smtp_encryption}
                onChange={(e) => setSettings({ ...settings, smtp_encryption: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="tls">TLS (Recommended)</option>
                <option value="ssl">SSL</option>
                <option value="none">None</option>
              </select>
            </div>

            <div>
              <Label htmlFor="smtp_from_email">From Email</Label>
              <p className="text-xs text-slate-500 mb-2">Email address shown as sender</p>
              <Input
                id="smtp_from_email"
                value={settings.smtp_from_email}
                onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                placeholder="noreply@example.com"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="smtp_from_name">From Name</Label>
              <p className="text-xs text-slate-500 mb-2">Display name for outgoing emails</p>
              <Input
                id="smtp_from_name"
                value={settings.smtp_from_name}
                onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                placeholder="Trap Tracker"
              />
            </div>
          </div>

          {/* Test Email Section */}
          {settings.smtp_enabled && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                <Send size={18} />
                Test Email Configuration
              </h4>
              <p className="text-sm text-blue-600 mb-3">
                Save your settings first, then send a test email to verify your SMTP configuration.
              </p>
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter test email address"
                  className="flex-1"
                />
                <Button
                  onClick={handleTestEmail}
                  disabled={testingEmail || hasChanges}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {testingEmail ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send Test
                    </>
                  )}
                </Button>
              </div>
              {hasChanges && (
                <p className="text-xs text-amber-600 mt-2">
                  * Please save your settings before sending a test email
                </p>
              )}
            </div>
          )}

          {/* SMTP Help */}
          <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
            <h4 className="font-medium text-slate-800 mb-2">Common SMTP Settings:</h4>
            <ul className="space-y-1">
              <li><strong>Gmail:</strong> smtp.gmail.com, Port 587 (TLS), use App Password</li>
              <li><strong>Outlook/Office 365:</strong> smtp.office365.com, Port 587 (TLS)</li>
              <li><strong>Yahoo:</strong> smtp.mail.yahoo.com, Port 587 (TLS)</li>
              <li><strong>Bluehost:</strong> mail.yourdomain.com, Port 465 (SSL) or 587 (TLS)</li>
            </ul>
            <p className="mt-2 text-xs text-slate-500">
              Note: For Gmail, you may need to create an App Password in your Google Account security settings.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* License Management */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl flex items-center gap-2">
            <Key className="text-[#1B3B6F]" />
            License Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LicenseStatusCard onRefresh={fetchSettings} />
        </CardContent>
      </Card>

      {/* Reset to Defaults */}
      <div className="flex justify-end">
        <Button 
          variant="outline" 
          onClick={handleResetToDefaults}
          className="text-red-600 hover:bg-red-50"
        >
          Reset to Default Settings
        </Button>
      </div>
    </div>
  );
}
